import React from "react";
import PropTypes from "prop-types";
import { C } from "../reporting/reportingStyles";

/**
 * The form look of the Digital Signage "Configure screen" page, as shared pieces.
 *
 * Copied from screen-configure.js rather than imported from it, because there they are local to
 * the page. Same tokens, same sizes, so a form built from these reads as the same product. That
 * page could adopt this file too; it was left alone here so its own tests stay untouched.
 *
 * Every piece is declared at module level on purpose: a component defined inside a render is a
 * new type on every render, React remounts it, and each input in it loses focus per keystroke.
 */

export const inputStyle = {
    padding: "9px 12px",
    fontSize: 13,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    width: "100%",
    transition: "border 0.15s",
};

export const smallInputStyle = { ...inputStyle, padding: "6px 10px", fontSize: 12 };

export const labelTextStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
    display: "block",
};

export const hintStyle = { fontSize: 12, color: C.textLight, marginTop: 6, lineHeight: 1.45, display: "block" };
export const warnStyle = { ...hintStyle, color: C.orange };

export const primaryBtnStyle = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    padding: "9px 22px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
};

export const secondaryBtnStyle = {
    background: C.white,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "9px 18px",
    fontWeight: 600,
    color: C.textMed,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
};

/** A disabled button keeps its colours and fades, as on the screens page. */
export const withDisabled = (style, disabled) => ({
    ...style,
    opacity: disabled ? 0.55 : 1,
    cursor: disabled ? "not-allowed" : style.cursor,
});

/** Two (or more) fields side by side, collapsing to one column on a narrow screen. */
export const Cols = ({ children, min = 180 }) => (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12 }}>
        {children}
    </div>
);

/** One titled group of settings: icon chip, title, optional subtitle and right-hand aside. */
export const Section = ({ icon: Icon, title, subtitle, children, aside }) => (
    <div
        style={{
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            background: C.white,
            padding: 18,
            display: "flex",
            flexDirection: "column",
            gap: 14,
            minWidth: 0,
        }}
    >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            {Icon && (
                <div
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        background: C.coralLight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon size={16} color={C.coral} />
                </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{title}</div>
                {subtitle && <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>{subtitle}</div>}
            </div>
            {aside}
        </div>
        {children}
    </div>
);

/**
 * A labelled field. The label text is exactly `label` (plus " *" when required), so a test or
 * a screen reader finds the field by the words an admin sees.
 */
export const Field = ({ label, required, children, hint }) => (
    <div style={{ minWidth: 0 }}>
        <label style={labelTextStyle}>
            {label}
            {required && <span style={{ color: C.coral }}> *</span>}
        </label>
        {children}
        {hint && (typeof hint === "string" ? <span style={hintStyle}>{hint}</span> : hint)}
    </div>
);

/**
 * An on/off switch with a label and an optional hint underneath.
 *
 * A real checkbox with role="switch" underneath the drawn track, so it is keyboard-operable and
 * reads as a switch. The hint sits outside the <label>, so the label's text is just its name.
 */
export const SwitchField = ({ checked, onChange, label, hint, disabled }) => (
    <div style={{ minWidth: 0 }}>
        <label
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                margin: 0,
                position: "relative",
            }}
        >
            <input
                type="checkbox"
                role="switch"
                checked={!!checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
            />
            <span
                aria-hidden
                style={{
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    background: checked ? C.coral : C.border,
                    position: "relative",
                    flexShrink: 0,
                    marginTop: 1,
                    transition: "background 0.15s",
                }}
            >
                <span
                    style={{
                        position: "absolute",
                        top: 2,
                        left: checked ? 18 : 2,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: C.white,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        transition: "left 0.15s",
                    }}
                />
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text, minWidth: 0 }}>{label}</span>
        </label>
        {hint && <div style={{ ...hintStyle, marginLeft: 48, marginTop: 2 }}>{hint}</div>}
    </div>
);

/**
 * The bar under a form: whether anything is unsaved, and the actions. Sticky, so the save button
 * is reachable from the bottom of a long tab without scrolling back.
 */
export const SaveBar = ({ dirty, children }) => (
    <div
        style={{
            position: "sticky",
            bottom: 0,
            zIndex: 5,
            marginTop: 16,
            padding: "12px 16px",
            background: C.white,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            boxShadow: "0 -4px 16px rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
        }}
    >
        <span style={{ fontSize: 12, fontWeight: 600, color: dirty ? C.orange : C.textLight }}>
            {dirty ? "● Unsaved changes" : "No unsaved changes"}
        </span>
        <div style={{ flex: 1 }} />
        {children}
    </div>
);

Section.propTypes = {
    icon: PropTypes.elementType,
    title: PropTypes.node.isRequired,
    subtitle: PropTypes.node,
    children: PropTypes.node,
    aside: PropTypes.node,
};
Field.propTypes = { label: PropTypes.node.isRequired, required: PropTypes.bool, children: PropTypes.node, hint: PropTypes.node };
SwitchField.propTypes = {
    checked: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.node.isRequired,
    hint: PropTypes.node,
    disabled: PropTypes.bool,
};
SaveBar.propTypes = { dirty: PropTypes.bool, children: PropTypes.node };
Cols.propTypes = { children: PropTypes.node, min: PropTypes.number };
